import React, { useState, useCallback, useEffect } from 'react';
import GeocodingService from '@mapbox/mapbox-sdk/services/geocoding';
import debounce from 'lodash.debounce';
import { Address } from 'services/person/person.types';
import './Address.scss';
import { ErrorMessage, FormikErrors } from 'formik';
import FormErrorMessage from 'components/FormErrorMessage/FormErrorMessage';

const MAPBOX_API_TOKEN = 'pk.eyJ1IjoiamVyb21lLWdldHVwIiwiYSI6ImNtNjd5MDE2czA4ZDIya3M5N2lsMnZ1a2QifQ.-KpyAiM-WTmlf-sngK4N6w';

const geocodingService = GeocodingService({ accessToken: MAPBOX_API_TOKEN });

type AddressAutocompleteProps = {
    baseField: string;
    formValue?: Address;
    setValue?: (field: string, value: React.SetStateAction<any>, shouldValidate?: boolean) => Promise<void | FormikErrors<any>>;
    id?: string;
};

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({ baseField = 'address', formValue, setValue, id }) => {

    useEffect(() => {
        if (formValue?.address && !query) {
            setQuery(formValue.address);
        }
    }, [formValue]);

    const updateField = (fieldName: string, value: string) => {
        setValue && setValue(`${baseField}.${fieldName}`, value);
    };

    const [query, setQuery] = useState<string>('');
    const [formData, setFormData] = useState<Address>({
        address: '',
        address_add: '',
        zipcode: '',
        city: '',
        country: ''
    });
    const [suggestions, setSuggestions] = useState<Address[]>([]);
    const [isAddressFocused, setIsAddressFocused] = useState<boolean>(false);

    const fetchSuggestions = async (value: string) => {
        if (value.length > 2) {
            try {
                const response = await geocodingService
                    .forwardGeocode({
                        query: value,
                        autocomplete: true,
                        limit: 5,
                        language: ['fr'],
                        proximity: [2.3522, 48.8566] // Coordonnées de Paris
                    })
                    .send();
                const features = response.body.features;
                const addresses = features.map((feature) => {
                    const zipcodeContext = feature.context?.find((ctx) => ctx.id.includes('postcode'));
                    const cityContext = feature.context?.find((ctx) => ctx.id.includes('place'));
                    const countryContext = feature.context?.find((ctx) => ctx.id.includes('country'));
                    return {
                        address: `${feature.address ? feature.address : ''} ${feature.text ? feature.text : ''}`,
                        address_add: '',
                        zipcode: zipcodeContext ? zipcodeContext.text : '',
                        city: cityContext ? cityContext.text : '',
                        country: countryContext ? countryContext.text : ''
                    };
                });
                setSuggestions(addresses);
            } catch (error) {
                console.error("Erreur lors de la recherche d'adresse :", error);
            }
        } else {
            setSuggestions([]);
        }
    };

    const debouncedFetchSuggestions = useCallback(
        debounce((value: string) => fetchSuggestions(value), 300),
        []
    );

    const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        let { name, value } = e.target;
        name = name.split('.')[1];

        if (name === 'address') {
            setQuery(value);
            debouncedFetchSuggestions(value);
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
        updateField(name, value);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.name.split('.')[1] === 'address') {
            setIsAddressFocused(true);
        } else {
            setIsAddressFocused(false);
            setSuggestions([]);
        }
    };

    const handleSuggestionClick = (suggestion: Address) => {
        setFormData(suggestion);
        setQuery(suggestion.address);

        // Mettre à jour tous les champs d'adresse
        updateField('address', suggestion.address);
        updateField('zipcode', suggestion.zipcode);
        updateField('city', suggestion.city);
        updateField('country', suggestion.country);

        setSuggestions([]);
    };

    return (
        <>
            <div className="searchable">
                <input
                    id={`${id || baseField}-address`}
                    type="text"
                    name={`${baseField}.address`}
                    placeholder="Adresse"
                    onChange={(e) => {
                        const value = e.target.value;
                        setQuery(value);
                        updateField('address', value);
                        fetchSuggestions(value); // Appel direct sans debounce pour tester
                    }}
                    value={formValue?.address || query}
                    onFocus={() => setIsAddressFocused(true)}
                    onBlur={(e) => {
                        // Retarder pour permettre le clic sur une suggestion
                        setTimeout(() => setIsAddressFocused(false), 200);
                    }}
                />
                {suggestions.length > 0 && isAddressFocused && (
                    <ul className="address-suggestions">
                        {suggestions.map((suggestion, index) => (
                            <li
                                key={index}
                                onClick={() => handleSuggestionClick(suggestion)}
                                style={{
                                    borderBottom: index < suggestions.length - 1 ? '1px solid #c5d8ef' : 'none'
                                }}
                            >
                                {`${suggestion.address}, ${suggestion.zipcode} ${suggestion.city}, ${suggestion.country}`}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <input
                type="text"
                value={formValue?.address_add ?? formData.address_add}
                onChange={handleInputChange}
                onFocus={handleFocus}
                placeholder="Complément d'adresse"
                name={`${baseField}.address_add`}
            />
            <FormErrorMessage field={`${baseField}.address_add`} />
            <input
                type="text"
                value={formValue?.zipcode ?? formData.zipcode}
                onChange={handleInputChange}
                onFocus={handleFocus}
                placeholder="Code postal"
                name={`${baseField}.zipcode`}
            />
            <FormErrorMessage field={`${baseField}.zipcode`} />
            <input
                type="text"
                value={formValue?.city ?? formData.city}
                onChange={handleInputChange}
                onFocus={handleFocus}
                placeholder="Ville"
                name={`${baseField}.city`}
            />
            <FormErrorMessage field={`${baseField}.city`} />
            <input
                type="text"
                value={formValue?.country ?? formData.country}
                onChange={handleInputChange}
                onFocus={handleFocus}
                placeholder="Pays"
                name={`${baseField}.country`}
            />
            <FormErrorMessage field={`${baseField}.country`} />
        </>
    );
};
